import React, { useEffect, useState, useRef } from "react";
import { AnalyticsData } from "../../api/Companies";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import LoadingSpinner from "../../shared-components/LoadingSpinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

type Props = {
  analyticsData: AnalyticsData | undefined;
};

const CHART_CONFIG = {
  candidateColor: "#FF5D22",
  otherCandidatesColor: "#325fc5",
  otherCandidatesBorder: "#325fc5",
};

function createChartConfiguration(
  analyticsData: AnalyticsData,
  chartHeight?: number
) {
  const distributionPoints = analyticsData.chart_ready_data.distribution_points;
  const highlightFlagValue =
    analyticsData.chart_ready_data.highlight_flag_value;

  const existsInDistribution = distributionPoints.some(
    (p) => p.flag_count === highlightFlagValue
  );

  let workingPoints = [...distributionPoints];

  if (!existsInDistribution) {
    let insertIndex = workingPoints.findIndex(
      (p) => p.flag_count > highlightFlagValue
    );

    if (insertIndex === -1) {
      const lastPoint = workingPoints[workingPoints.length - 1];

      const maxFlagCount = lastPoint.flag_count;
      const flagDifference = highlightFlagValue - maxFlagCount;

      let interpolatedPercent;
      if (lastPoint.cumulative_percent >= 99) {
        interpolatedPercent = Math.min(
          100,
          lastPoint.cumulative_percent +
            (100 - lastPoint.cumulative_percent) * 0.9
        );
      } else {
        const secondLastPoint =
          workingPoints.length > 1
            ? workingPoints[workingPoints.length - 2]
            : null;
        if (secondLastPoint) {
          const lastSegmentChange =
            lastPoint.cumulative_percent - secondLastPoint.cumulative_percent;
          const lastFlagChange =
            lastPoint.flag_count - secondLastPoint.flag_count;
          const rate = lastSegmentChange / lastFlagChange;

          interpolatedPercent = Math.min(
            99.5,
            lastPoint.cumulative_percent + flagDifference * rate * 0.5
          );
        } else {
          interpolatedPercent = Math.min(
            99.5,
            lastPoint.cumulative_percent +
              (100 - lastPoint.cumulative_percent) * 0.8
          );
        }
      }

      workingPoints.push({
        flag_count: highlightFlagValue,
        candidate_count: 0,
        cumulative_percent: interpolatedPercent,
      });
    } else if (insertIndex === 0) {
      const firstPoint = workingPoints[0];
      const interpolatedPercent = firstPoint.cumulative_percent * 0.5;

      workingPoints.unshift({
        flag_count: highlightFlagValue,
        candidate_count: 0,
        cumulative_percent: interpolatedPercent,
      });
    } else {
      const prevPoint = workingPoints[insertIndex - 1];
      const nextPoint = workingPoints[insertIndex];

      const ratio =
        (highlightFlagValue - prevPoint.flag_count) /
        (nextPoint.flag_count - prevPoint.flag_count);
      const interpolatedPercent =
        prevPoint.cumulative_percent +
        (nextPoint.cumulative_percent - prevPoint.cumulative_percent) * ratio;

      workingPoints.splice(insertIndex, 0, {
        flag_count: highlightFlagValue,
        candidate_count: 0,
        cumulative_percent: interpolatedPercent,
      });
    }
  }

  let xAxisMin = 0;

  xAxisMin = Math.max(0, xAxisMin);

  const labels = workingPoints.map((point) => point.flag_count.toString());

  const floatingBarData = workingPoints.map((point, index) => {
    const start =
      index === 0 ? xAxisMin : workingPoints[index - 1].cumulative_percent;
    const end = point.cumulative_percent;
    return [start, end];
  });

  const backgroundColors = workingPoints.map((point) => {
    return point.flag_count === highlightFlagValue
      ? CHART_CONFIG.candidateColor
      : CHART_CONFIG.otherCandidatesColor;
  });

  const borderColors = workingPoints.map((point) => {
    return point.flag_count === highlightFlagValue
      ? CHART_CONFIG.candidateColor
      : CHART_CONFIG.otherCandidatesBorder;
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Cumulative Percentile",
        data: floatingBarData,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 0.2,
        minBarLength: 1,
        categoryPercentage: 0.85,
        barPercentage: 1.0,
      },
    ],
  };

  const percentageAnnotations: Record<string, any> = {};

  // Calculate bar thickness based on chart configuration
  const categoryPercentage = 0.85;
  const barPercentage = 1.0;

  // Calculate actual bar thickness from chart height if available
  let barThickness: number;
  if (chartHeight) {
    // Calculate based on actual chart height
    const chartArea = chartHeight - 60; // Subtract padding/margins (top + bottom)
    const heightPerCategory = chartArea / workingPoints.length;
    barThickness = heightPerCategory * categoryPercentage * barPercentage;
  } else {
    // Fallback to estimated value
    const estimatedHeightPerCategory = 45;
    barThickness =
      estimatedHeightPerCategory * categoryPercentage * barPercentage;
  }

  // Calculate font size based on bar thickness
  const calculateFontSize = (): number => {
    const minSize = 8;
    const maxSize = 12;

    // Scale font size based on bar thickness
    // Thin bars (< 20px) get smaller fonts, thick bars (> 40px) get larger fonts
    if (barThickness < 15) return minSize;
    if (barThickness < 20) return 9;
    if (barThickness < 30) return 10;
    if (barThickness < 40) return 11;
    return maxSize;
  };

  const fontSize = calculateFontSize();

  workingPoints.forEach((point, index) => {
    // Calculate yAdjust: half bar height + font size + padding for consistent positioning
    const yAdjust = barThickness / 2 + 20;

    percentageAnnotations[`label${index}`] = {
      type: "label" as const,
      xValue: point.cumulative_percent,
      yValue: index,
      content: `${point.cumulative_percent.toFixed(1)}%`,
      color: "#333",
      font: { size: fontSize, weight: "normal" as const },
      textAlign: "left" as const,
      position: "end" as const,
      xAdjust: 2,
      yAdjust: yAdjust,
      display: true,
    };

    percentageAnnotations[`line${index}`] = {
      type: "line" as const,
      xMin: point.cumulative_percent,
      xMax: point.cumulative_percent,
      yMin: index - 0,
      yMax: index + 0.4,
      borderColor: "#c8c8c8",
      borderWidth: 1,
      display: true,
    };
  });

  const options: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    layout: {
      padding: {
        top: 0,
        right: 80,
        bottom: 0,
        left: 10,
      },
    },
    clip: {
      bottom: 0,
      top: 0,
      left: 0,
      right: 20,
    },
    plugins: {
      title: { display: false },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      annotation: {
        annotations: percentageAnnotations,
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        position: "bottom" as const,
        title: {
          display: true,
          text: "Cumulative Percentile (%)",
          font: { size: 12 },
        },
        min: xAxisMin,
        max: 100,
        ticks: {
          display: true,
          stepSize: 10,
          callback: function (value) {
            return value + "%";
          },
        },
        grid: {
          display: true,
          color: "#e5e5e5",
        },
      },
      y: {
        type: "category" as const,
        position: "left" as const,
        title: {
          display: true,
          text: "Misconduct Flags",
          font: { size: 12 },
        },
        ticks: {
          display: true,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return { data, options };
}

const ReportsSummaryChartFlagsPercentile = ({ analyticsData }: Props) => {
  const chartRef = useRef<ChartJS<"bar", number[][], unknown>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartKey, setChartKey] = useState(0);
  const [chartData, setChartData] = useState<ReturnType<
    typeof createChartConfiguration
  > | null>(null);
  const [chartImageUrl, setChartImageUrl] = useState<string>("");

  // Measure container and create chart configuration with actual height
  useEffect(() => {
    if (!analyticsData || !containerRef.current) return;

    const updateChartConfig = () => {
      setChartKey((prev) => prev + 1);
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;

      // Only create chart data once we have a valid container height
      if (containerHeight > 0) {
        const config = createChartConfiguration(analyticsData, containerHeight);
        setChartData(config);
      }
    };

    // Initial creation
    updateChartConfig();

    // Add resize listener
    window.addEventListener("resize", updateChartConfig);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateChartConfig);
    };
  }, [analyticsData]);

  // Capture chart as data URL after it's rendered
  useEffect(() => {
    if (chartRef.current) {
      // Use a slight delay to ensure the chart is fully rendered
      const timer = setTimeout(() => {
        if (chartRef.current) {
          const canvas = chartRef.current.canvas;
          const imageUrl = canvas.toDataURL("image/png");
          setChartImageUrl(imageUrl);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [chartData, chartKey]);

  if (!chartData) {
    return (
      <div ref={containerRef} className="w-full h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden print:hidden"
      >
        <Bar
          key={chartKey}
          ref={chartRef}
          data={chartData.data}
          options={chartData.options}
        />
      </div>
      <div className="hidden print:flex items-center justify-center w-full h-full">
        {chartImageUrl && (
          <img
            src={chartImageUrl}
            alt="Flags Percentile Chart"
            className="h-52 object-contain"
          />
        )}
      </div>
    </>
  );
};

export default ReportsSummaryChartFlagsPercentile;
